import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaBox,
  FaBoxOpen,
  FaChartBar,
  FaChevronDown,
  FaChevronRight,
  FaCog,
  FaCogs,
  FaCrown,
  FaDonate,
  FaGift,
  FaMoneyCheckAlt,
  FaRegBell,
  FaRegCreditCard,
  FaRegFileAlt,
  FaRegFileArchive,
  FaRegListAlt,
  FaTruck,
  FaUser,
  FaUserCog,
  FaUserFriends,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import type { NavItem } from "../../config/navigation";

interface SidebarNavProps {
  nav: NavItem[];
}

const iconMap: Record<string, JSX.Element> = {
  dashboard: <FaChartBar className="text-lg" />,
  businessPlan: <FaRegFileAlt className="text-lg" />,
  userManagement: <FaUserCog className="text-lg" />,
  profile: <FaUser className="text-base" />,
  activateDeactivateUsers: <FaUserFriends className="text-base" />,
  promoteUserToModerator: <FaCrown className="text-base" />,
  commissions: <FaMoneyCheckAlt className="text-lg" />,
  myCommissions: <FaRegCreditCard className="text-base" />,
  allCommissions: <FaMoneyCheckAlt className="text-base" />,
  teamCommissions: <FaUserFriends className="text-base" />,
  commissionAnalytics: <FaChartBar className="text-base" />,
  manualApproval: <FaRegFileAlt className="text-base" />,
  referralsConfig: <FaCogs className="text-base" />,
  referralsAnalytics: <FaChartBar className="text-base" />,
  referralsWithdrawals: <FaMoneyCheckAlt className="text-base" />,
  referralsUserNetwork: <FaUserFriends className="text-base" />,
  teamNetwork: <FaUserFriends className="text-base" />,
  orders: <FaBoxOpen className="text-lg" />,
  orderManagement: <FaBoxOpen className="text-base" />,
  registrationOrders: <FaUserFriends className="text-base" />,
  normalOrders: <FaBoxOpen className="text-base" />,
  myOrders: <FaBoxOpen className="text-base" />,
  donations: <FaGift className="text-base" />,
  adminDonations: <FaDonate className="text-base" />,
  emergency: <FaRegBell className="text-lg" />,
  emergencyWithdrawals: <FaRegFileAlt className="text-base" />,
  adminEmergencyWithdrawals: <FaRegFileArchive className="text-base" />,
  products: <FaBoxOpen className="text-lg" />,
  manageProducts: <FaBoxOpen className="text-base" />,
  manageCategories: <FaRegListAlt className="text-base" />,
  manageShipping: <FaTruck className="text-base" />,
  addProduct: <FaBox className="text-base" />,
  managePayments: <FaRegCreditCard className="text-base" />,
  analytics: <FaChartBar className="text-lg" />,
  adminAnalytics: <FaChartBar className="text-base" />,
  teamAnalytics: <FaChartBar className="text-base" />,
  logs: <FaRegListAlt className="text-lg" />,
  dispatchManagement: <FaTruck className="text-lg" />,
  dispatch: <FaTruck className="text-lg" />,
  configuration: <FaCog className="text-lg" />,
  systemConfig: <FaCog className="text-base" />,
  payments: <FaRegCreditCard className="text-lg" />,
  referrals: <FaUserFriends className="text-lg" />,
  changePassword: <FaCog className="text-base" />,
};

const SidebarNav = ({ nav }: SidebarNavProps) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  // Expand section if any child is active
  const getInitialOpenSections = () => {
    const initial: Record<string, boolean> = {};
    nav.forEach((item) => {
      if (item.children && item.children.length > 0) {
        initial[item.labelKey] = item.children.some(
          (child) => child.to && pathname === child.to
        );
      }
    });
    return initial;
  };
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(
    getInitialOpenSections()
  );

  const handleToggle = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <nav className="flex flex-col gap-1 w-full px-3 pb-4">
      {nav.map((item) => {
        const isActive = item.to && pathname === item.to;
        const hasChildren = !!(item.children && item.children.length > 0);
        return (
          <div key={item.labelKey || item.to} className="mb-1">
            <div
              className={`flex items-center gap-3 w-full px-3 py-3 rounded-lg font-medium text-sm focus:outline-none transition-all duration-200 relative
                ${
                  hasChildren
                    ? "cursor-pointer hover:bg-gray-100"
                    : "hover:bg-gray-100"
                }
                ${
                  isActive
                    ? "bg-blue-50 text-blue-700 border-r-2 border-blue-600"
                    : "text-gray-700 hover:text-gray-900"
                }
              `}
              onClick={
                hasChildren ? () => handleToggle(item.labelKey) : undefined
              }
              aria-expanded={
                hasChildren ? !!openSections[item.labelKey] : undefined
              }
              aria-controls={
                hasChildren ? `section-${item.labelKey}` : undefined
              }
              tabIndex={0}
            >
              {iconMap[item.labelKey] || <FaRegListAlt className="text-lg" />}
              <span>{t(`navigation.${item.labelKey}`)}</span>
              {hasChildren && (
                <span className="ml-auto">
                  {openSections[item.labelKey] ? (
                    <FaChevronDown />
                  ) : (
                    <FaChevronRight />
                  )}
                </span>
              )}
              {!hasChildren && item.to && (
                <Link
                  to={item.to}
                  className="absolute inset-0 z-10"
                  tabIndex={-1}
                  aria-label={t(`navigation.${item.labelKey}`)}
                />
              )}
            </div>
            {hasChildren &&
              Array.isArray(item.children) &&
              item.children.length > 0 && (
                <div
                  id={`section-${item.labelKey}`}
                  className={`flex flex-col gap-1 pl-6 transition-all duration-200 overflow-hidden ${
                    openSections[item.labelKey]
                      ? "max-h-96 opacity-100 mt-2"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  {item.children.map((child) => (
                    <Link
                      key={child.to}
                      to={child.to!}
                      className={`py-2 px-3 rounded-lg font-medium text-sm transition-all duration-150 flex items-center gap-3
                      ${
                        pathname === child.to
                          ? "bg-blue-50 text-blue-700 border-r-2 border-blue-600"
                          : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                      }
                    `}
                      tabIndex={0}
                    >
                      {iconMap[child.labelKey] || (
                        <FaRegFileAlt className="text-base" />
                      )}
                      <span>{t(`navigation.${child.labelKey}`)}</span>
                    </Link>
                  ))}
                </div>
              )}
          </div>
        );
      })}
    </nav>
  );
};

export default SidebarNav;
